import {signalStore, withState, withMethods, patchState, withComputed} from '@ngrx/signals';
import {ILocation} from '../../types/ILocation';
import {ICart} from '../../types/ICart';
import {rxMethod} from '@ngrx/signals/rxjs-interop';
import {catchError, forkJoin, of, pipe, switchMap, tap} from 'rxjs';
import {computed, inject} from '@angular/core';
import {CartService} from '../services/cart.service';
import {IPriceLevel} from '../../types/IPriceLevel';
import {IBrand} from '../../types/IBrand';
import {IVendor} from '../../types/IVendor';
import {IPriority} from '../../types/IPriority';
import {ClientSettingsService} from '../services/client-settings.service';
import {IVendorPriority} from '../../types/IVendorPriority';
import {ClientVendorsService} from '../services/client-vendors.service';
import {ToastService} from '../services/toast.service';
import {withHooks} from '@ngrx/signals';
import {BreakpointObserver} from '@angular/cdk/layout';
import {CartSnapshotService} from '../services/cart-snapshot.service';
import {setError, setFulfilled, setPending, withRequestStatus} from './features/with-request-status';

type AppState = {
  client_id: string;
  session_id: string;
  location: ILocation;

  cart: ICart;

  price_levels: IPriceLevel[];
  settings: Record<string, any>;

  brands: IBrand[];
  client_vendors: IVendor[];

  brand_vendor_priorities: IVendorPriority[];
  brand_priorities: IPriority[];

  currentBreakpoint: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

  permissions: Record<string, boolean>;
  capabilities: Record<string, boolean>;
};

export const AppStore = signalStore(
  {providedIn: 'root'},
  withState<AppState>({
    client_id: '',
    session_id: '',
    location: {name: '', phone: ''},
    cart: {
      id: '',
      client_id: '',
      search_request: {},
      options: {
        use_addons: false,
        use_tire_price_optimizer: false,
      },
      repair_order: {},
      webhooks: {},
      snapshot: null,
    },
    price_levels: [],
    settings: {},
    brands: [],
    client_vendors: [],
    brand_vendor_priorities: [],
    brand_priorities: [],
    permissions: {},
    capabilities: {},
    currentBreakpoint: (() => {
      const w = window.innerWidth;
      if (w < 640) return 'xs';
      if (w < 768) return 'sm';
      if (w < 1024) return 'md';
      if (w < 1280) return 'lg';
      if (w < 1536) return 'xl';
      return '2xl';
    })(),
  }),
  withRequestStatus('cart'),
  withHooks((store) => ({
    onInit() {
      const breakpointObserver = inject(BreakpointObserver);
      breakpointObserver.observe([
        '(min-width: 640px)',
        '(min-width: 768px)',
        '(min-width: 1024px)',
        '(min-width: 1280px)',
        '(min-width: 1536px)'
      ]).pipe(
        tap(state => {
          const bp = state.breakpoints;
          let currentBreakpoint: AppState['currentBreakpoint'] = 'xs';
          if (bp['(min-width: 1536px)']) currentBreakpoint = '2xl';
          else if (bp['(min-width: 1280px)']) currentBreakpoint = 'xl';
          else if (bp['(min-width: 1024px)']) currentBreakpoint = 'lg';
          else if (bp['(min-width: 768px)']) currentBreakpoint = 'md';
          else if (bp['(min-width: 640px)']) currentBreakpoint = 'sm';
          patchState(store, {currentBreakpoint});
        })
      ).subscribe();
    }
  })),
  withComputed((store) => ({
    defaultPriceLevel: computed(() => store.price_levels()?.find(level => level.id == store.cart().repair_order?.price_level_id) || store.price_levels()?.find(level => level.is_default) || store.price_levels()?.at(0)),

    getSetting: computed(() => (key: string) => store.settings()[key]),
  })),
  withMethods(
    (
      store,
      cartService = inject(CartService),
      clientSettingsService = inject(ClientSettingsService),
      clientVendorsService = inject(ClientVendorsService),
      toastService = inject(ToastService),
      cartSnapshotService = inject(CartSnapshotService)
    ) => ({
      initialize: rxMethod<{ session_id: string }>(
        pipe(
          tap((params) => patchState(store, setPending('cart'), {session_id: params.session_id})),
          switchMap((params) =>
            cartService.getCartSession(params.session_id).pipe(
              switchMap((session) => {
                patchState(store, {
                  client_id: session.client_id,
                  location: session.location,
                  permissions: session.permissions,
                  capabilities: session.capabilities,
                });

                return forkJoin({
                  cart: of(session.cart),
                  priceLevels: cartService.getPriceLevels(session.client_id),
                  settings: clientSettingsService.getSettings(session.client_id),
                  brand_priorities: clientSettingsService.getBrandPriorities(session.client_id),
                });
              }),
              tap(({cart, priceLevels, settings, brand_priorities}) => {
                patchState(store, setFulfilled('cart'), {
                  cart,
                  price_levels: priceLevels,
                  settings,
                  brand_priorities,
                });
              }),
              catchError((error) => {
                patchState(store, setError('cart', error.message));
                return of(null);
              })
            )
          )
        )
      ),

      getClientVendors: rxMethod<void>(
        pipe(
          switchMap(() => clientVendorsService.getVendors(store.client_id()).pipe(
            tap((client_vendors: IVendor[]) => patchState(store, {client_vendors}))
          ))
        )),

      getBrands: rxMethod<void>(
        pipe(
          switchMap(() => clientSettingsService.getBrands(store.client_id()).pipe(
            tap((brands: IBrand[]) => patchState(store, {brands}))
          ))
        )),

      getVendorPriorities: rxMethod<void>(
        pipe(
          switchMap(() => clientSettingsService.getBrandVendorPriorities(store.client_id()).pipe(
            tap((brand_vendor_priorities: IVendorPriority[]) => patchState(store, {brand_vendor_priorities}))
          ))
        )),

      updateBrandPriorities: (priorities: IPriority[]) => {
        const previousPriorities = store.brand_priorities();
        patchState(store, {brand_priorities: priorities});

        clientSettingsService.updateBrandPriorities(store.client_id(), priorities).subscribe({
          next: () => {
            toastService.success('Brand priorities updated');
          },
          error: (err) => {
            patchState(store, {brand_priorities: previousPriorities});
            toastService.error('Failed to save brand priorities');
          }
        });
      },

      updateVendorPriorities: (brand_id: string | number, priorities: IVendorPriority[]) => {
        const previousPriorities = store.brand_vendor_priorities();
        const filteredPriorities = previousPriorities.filter(vp => vp.brand_id != brand_id);
        patchState(store, {brand_vendor_priorities: [...filteredPriorities, ...priorities]});

        clientSettingsService.updateBrandVendorPriorities(store.client_id(), brand_id, priorities).subscribe({
          next: () => {
            toastService.success('Vendor priorities updated');
          },
          error: (err) => {
            patchState(store, {brand_vendor_priorities: previousPriorities});
            toastService.error('Failed to save vendor priorities');
          }
        });
      },

      clearSnapshot: () => {
        const snapshot = store.cart().snapshot;
        patchState(store, {cart: {...store.cart(), snapshot: null}});

        if (snapshot) {
          cartSnapshotService.destroy(snapshot.token).subscribe({
            error: (err) => {
              patchState(store, {cart: {...store.cart(), snapshot: snapshot}});
              toastService.error('Failed to clear snapshot');
            }
          });
        }
      },

      updateSetting: (key: string, value: any) => {
        const previousValue = store.settings()[key];
        patchState(store, {settings: {...store.settings(), [key]: {...previousValue, value: value}}});

        clientSettingsService.updateSetting(store.client_id(), key, value).subscribe({
          next: () => {
            toastService.success('Settings updated');
          },
          error: (err) => {
            patchState(store, {settings: {...store.settings(), [key]: previousValue}});
            toastService.error('Failed to update setting');
          }
        });
      },
    }),
  )
);
